import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, ArrowLeft, Save, X, MapPin } from 'lucide-react';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabase';

const CityManager = () => {
    const navigate = useNavigate();
    const [cities, setCities] = useState([]);
    const [loading, setLoading] = useState(true);

    // Add Form
    const [newCity, setNewCity] = useState('');

    // Edit Form
    const [editingId, setEditingId] = useState(null);
    const [editName, setEditName] = useState('');

    useEffect(() => {
        fetchCities();
    }, []);

    const fetchCities = async () => {
        setLoading(true);
        const { data } = await supabase
            .from('cities')
            .select('*')
            .order('name', { ascending: true });

        if (data) setCities(data);
        setLoading(false);
    };

    const handleAdd = async (e) => {
        e.preventDefault();
        const cityToSave = newCity.trim();

        if (!cityToSave) return;

        try {
            const { error } = await supabase
                .from('cities')
                .insert([{ name: cityToSave, sector: 'Ottange' }]); // Default sector as fallback for schema

            if (error) throw error;
            setNewCity('');
            fetchCities();
        } catch (error) {
            alert('Erreur: ' + error.message);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Supprimer cette ville ?')) return;
        await supabase.from('cities').delete().eq('id', id);
        fetchCities();
    };

    const startEdit = (city) => {
        setEditingId(city.id);
        setEditName(city.name);
    };

    const saveEdit = async () => {
        if (!editName.trim()) return;
        await supabase.from('cities').update({ name: editName.trim() }).eq('id', editingId);
        setEditingId(null);
        fetchCities();
    };

    return (
        <div className="max-w-4xl mx-auto py-12 px-4 font-sans">
            <div className="flex items-center gap-4 mb-8">
                <button onClick={() => navigate('/admin')} className="p-3 bg-white shadow-sm border border-gray-100 rounded-2xl hover:bg-gray-50 transition-all">
                    <ArrowLeft size={20} className="text-[#2C353D]" />
                </button>
                <div>
                    <h1 className="text-3xl font-black text-[#2C353D]">Gestion des Villes</h1>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mt-1">Espace Ottange</p>
                </div>
            </div>

            <div className="bg-white p-8 rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 mb-10">
                <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
                    <Plus size={14} className="text-[#EA723D]" /> Ajouter une ville
                </h3>
                <form onSubmit={handleAdd} className="flex gap-4 items-center">
                    <div className="flex-1">
                        <input
                            type="text"
                            value={newCity}
                            onChange={(e) => setNewCity(e.target.value)}
                            placeholder="Nom de la ville (ex: Ottange)"
                            className="w-full p-4 border-2 border-gray-50 bg-gray-50 rounded-2xl outline-none focus:border-[#EA723D] focus:bg-white font-bold transition-all"
                        />
                    </div>

                    <Button type="submit" disabled={!newCity.trim() || loading} className="h-[56px] px-8 bg-[#2C353D] text-white rounded-2xl font-black shadow-lg shadow-gray-200">
                        Ajouter
                    </Button>
                </form>
            </div>

            <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="flex items-center justify-between mb-6 px-4">
                    <h3 className="text-xs font-black text-[#EA723D] uppercase tracking-widest">Liste des Villes ({cities.length})</h3>
                    <div className="h-px flex-1 bg-gray-100 mx-4"></div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {cities.map(city => (
                        <div key={city.id} className="group/item flex items-center justify-between p-3 rounded-xl border border-transparent hover:border-gray-100 hover:bg-gray-50/50 transition-all">
                            {editingId === city.id ? (
                                <div className="flex-1 flex items-center gap-2">
                                    <input
                                        type="text"
                                        value={editName}
                                        onChange={(e) => setEditName(e.target.value)}
                                        className="flex-1 p-2 border-2 border-[#EA723D] rounded-xl bg-white font-bold text-sm"
                                        autoFocus
                                        onKeyDown={(e) => e.key === 'Enter' && saveEdit()}
                                    />
                                    <button onClick={saveEdit} className="p-2 text-green-600 hover:bg-green-50 rounded-xl transition-colors"><Save size={18} /></button>
                                    <button onClick={() => setEditingId(null)} className="p-2 text-gray-400 hover:bg-gray-100 rounded-xl transition-colors"><X size={18} /></button>
                                </div>
                            ) : (
                                <>
                                    <span className="font-bold text-[#2C353D] text-sm flex items-center gap-2">
                                        <MapPin size={14} className="text-gray-300" />
                                        {city.name}
                                    </span>
                                    <div className="flex gap-1 opacity-0 group-hover/item:opacity-100 transition-opacity">
                                        <button onClick={() => startEdit(city)} className="p-2 text-gray-300 hover:text-[#EA723D] hover:bg-orange-50 rounded-lg transition-colors"><Save size={16} className="rotate-90 scale-75" /></button>
                                        <button onClick={() => handleDelete(city.id)} className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"><Trash2 size={16} /></button>
                                    </div>
                                </>
                            )}
                        </div>
                    ))}
                </div>
                {cities.length === 0 && !loading && (
                    <p className="text-center py-12 text-gray-400 text-sm italic">Aucune ville enregistrée.</p>
                )}
            </div>
        </div>
    );
};

export default CityManager;
